import type {
  CellStateInfo,
  RecognitionCell,
  RecognitionCellSelection,
  RecognitionContractRef,
  RecognitionLine,
  RecognitionResponse,
} from "./contracts";

export interface RecognitionTableFacts {
  months: string[];
  rows: Array<{ k: string; book: string | null; trader: string | null }>;
  cellAt: Map<string, RecognitionCell>;
  stateAt: Map<string, CellStateInfo>;
  bookedByMonth: Map<string, number>;
  staleContracts: string[];
}

export function recognitionMonthLabel(ym: string): string {
  const [year, month] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(month) - 1]} ${year!.slice(2)}`;
}

export function recognitionContractLabel(contract: RecognitionContractRef): string {
  return `${contract.code} ${recognitionMonthLabel(`${contract.expYear}-${String(contract.expMonth).padStart(2, "0")}`)}`;
}

/** Desktop row label. Kept byte-for-byte compatible with the existing route. */
export function recognitionRowLabel(book: string | null, trader: string | null): string {
  return `${book ?? "—"}·${trader ?? "—"}`;
}

/** Existing lookup identity used by the desktop table and close-state overlay. */
export function recognitionCellLookupKey(
  book: string | null,
  trader: string | null,
  month: string
): string {
  return `${recognitionRowLabel(book, trader)}|${month}`;
}

export function recognitionContractKey(contract: RecognitionContractRef): string {
  return `${contract.code}|${contract.expYear}|${contract.expMonth}`;
}

export function selectSignedUnitMargin(line: RecognitionLine): number {
  return line.side === "S" ? line.entry - line.exit : line.exit - line.entry;
}

/** A future scheduled final result is not an approved accounting headline. */
export function selectLatestApprovedMonth(data: RecognitionResponse): { month: string; bookedEUR: number } | null {
  const months = [...new Set(data.cellStates.filter((s) => s.month < data.asOf.slice(0, 7)).map((s) => s.month))].sort().reverse();
  for (const month of months) {
    const states = data.cellStates.filter((s) => s.month === month);
    const live = data.cells.filter((c) => c.month === month);
    if (!states.length || states.some((s) => s.state !== "hard" || s.snapshotTotalEur == null)) continue;
    if (live.some((c) => c.estimateCount > 0 || c.fxMissingCount > 0 || !states.some((s) => s.book === c.book && s.trader === c.trader))) continue;
    return { month, bookedEUR: Math.round(states.reduce((sum, s) => sum + s.snapshotTotalEur!, 0) * 100) / 100 };
  }
  return null;
}

export function selectBookedVsSnapshotDrift(
  cell: RecognitionCell,
  state: CellStateInfo | null = null
): number {
  return (
    state?.state === "hard" && state.snapshotTotalEur != null
      ? Math.round((cell.bookedEUR - state.snapshotTotalEur) * 100) / 100
      : 0
  );
}

export function selectRecognitionTableFacts(data: RecognitionResponse): RecognitionTableFacts {
  const stateAt = new Map(
    data.cellStates.map((state) => [
      recognitionCellLookupKey(state.book, state.trader, state.month),
      state,
    ])
  );
  const months = [...new Set(data.cells.map((cell) => cell.month))].sort();
  const rows = [
    ...new Map(
      data.cells.map((cell) => [
        recognitionRowLabel(cell.book, cell.trader),
        { book: cell.book, trader: cell.trader },
      ])
    ).entries(),
  ]
    .map(([k, value]) => ({ k, ...value }))
    .sort((a, b) => a.k.localeCompare(b.k));
  const cellAt = new Map(
    data.cells.map((cell) => [
      recognitionCellLookupKey(cell.book, cell.trader, cell.month),
      cell,
    ])
  );
  const bookedByMonth = new Map(data.months.map((month) => [month.month, month.bookedEUR]));
  const staleContracts = [
    ...new Set(
      data.lines
        .filter((line) => line.finality === "stale")
        .map((line) => recognitionContractKey(line.contract))
    ),
  ];
  return { stateAt, months, rows, cellAt, bookedByMonth, staleContracts };
}

export function selectRecognitionLines(
  data: RecognitionResponse,
  selection: RecognitionCellSelection | null
): RecognitionLine[] {
  if (!selection) return [];
  return data.lines.filter(
    (line) =>
      line.book === selection.book &&
      line.trader === selection.trader &&
      line.recognitionMonth === selection.month
  );
}
